import React, { useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import { Alert, Button, Descriptions, Space, Tag, Typography } from 'antd';
import { CheckCircleOutlined, EditOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { routeHistory } from '@nackle/biw-web-router';
import { Routes } from '../../../routes';
import Spinner from '../../components/Spinner/Spinner';
import DeleteButton from '../../components/DeleteButton/DeleteButton';
import { renderWithSearchWords } from '../../components/SearchBar/SearchBar';
import useDeleteObjective from '../../gql/hooks/useDeleteObjective'; 
import SearchQueryList from '../../components/SearchQueryList/SearchQueryList';
import OBJECTIVE_CRITERIA_TYPES_QUERY from '../../gql/queries/OBJECTIVE_CRITERIA_TYPES_QUERY';
import { useFeatureFlags } from '../../../featureFlags';
import { flags } from '../../../featureFlags/flags';
import { ColoredTag } from '../GenericCreateEdit/GenericElements';
import { CriteriaTable, formatCriteriaItem, sortCriteria } from '../../../pages/CreateEditObjectiveView/CreateEditCriteria';
import { DEFAULT_LOCALE_CODE } from '../../../const';
import './styles.less';
let expandedRows = [];

/**
 * Expanded row for one objective. Everything here comes off the search result the list already has -
 * nothing is fetched except the course names the criteria table looks up for itself.
 *
 * @param {Object} props.rewardsEnabled which reward types the company has turned on; only those get
 *  a spot, since a reward that cannot be awarded is not worth a column
 */
const ObjectiveExpandedRow = ( { objective, criteriaTypes, rewardsEnabled } ) => {

    // the locale picker is gone, so this always reads the default locale
    const description = useMemo(
        () => objective.locales?.find( l => l.code === DEFAULT_LOCALE_CODE )?.description,
        [ objective.locales ] );

    const criteria = useMemo(
        () => sortCriteria( ( objective.criteria ?? [] ).map( formatCriteriaItem ) ),
        [ objective.criteria ] );

    // one row per reward type the company allows, nested inside the single Rewards spot below
    const rewardItems = [
        rewardsEnabled.points && {
            key: 'points',
            label: 'Points',
            children: objective.points > 0 ?
                objective.points :
                <span className='canceled'>No points</span>
        },
        rewardsEnabled.credits && {
            key: 'credits',
            label: 'Credits',
            children: objective.credits > 0 ?
                objective.credits :
                <span className='canceled'>No credits</span>
        },
        rewardsEnabled.badge && {
            key: 'badge',
            label: 'Badge',
            children: objective.badgeId ?
                <CheckCircleOutlined style={ { color: 'green', alignSelf: 'center' } } /> :
                <span className='canceled'>No badge</span>
        },
    ].filter( Boolean );

    return (
        <div className="row-expand">
            <Descriptions bordered layout='vertical' column={ 2 } items={ [
                {
                    key: 'description',
                    label: `Description (${ DEFAULT_LOCALE_CODE })`,
                    span: 2,
                    children: description?.length > 0 ?
                        <Typography.Paragraph ellipsis={ { rows: 6 } }>
                            <span dangerouslySetInnerHTML={ { __html: DOMPurify.sanitize( description ) } } />
                        </Typography.Paragraph> :
                        <span className='canceled'>No description</span>
                },
                {
                    key: 'repeatable',
                    label: 'Repeatable',
                    children: <ColoredTag.Repeatable isRepeatable={ objective.isRepeatable } />
                },
                ...( rewardItems.length > 0 ? [ {
                    key: 'rewards',
                    label: 'Rewards',
                    children: <Descriptions className='objective-rewards' size='small' column={ 1 } items={ rewardItems } />
                } ] : [] ),
                {
                    key: 'criteria',
                    label: 'Criteria',
                    span: 2,
                    children: criteria.length > 0 ?
                        <CriteriaTable criteria={ criteria } criteriaTypes={ criteriaTypes } /> :
                        <span className='canceled'>No criteria</span>
                },
            ] } />
        </div>
    );
};

const ObjectivesListPage = () => { 
    const listPageRef = useRef( null );

    const { deleteObjective, isDeleting } = useDeleteObjective();

    const [ showBrands, POINTS_REWARD_ENABLED, CUSTOM_REWARD_ENABLED, BADGE_REWARD_ENABLED ] = 
        useFeatureFlags( [ flags.BRANDS, flags.POINTS_REWARD_ENABLED, flags.CREDITS_REWARD_ENABLED, flags.BADGES_REWARD_ENABLED ] );

    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Incentives', path: Routes.GamificationNav.buildPath() },
        { name: 'Objectives' }
    ];

    const uiStrings = {
        cardTitle: 'Objectives',
        createButtonTitle: 'Create New Objective'
    };
    
    const { data: criteriaTypesData, loading: criteriaTypesLoading, 
        error: criteriaTypesError, called: criteriaTypesCalled } = 
        useQuery( OBJECTIVE_CRITERIA_TYPES_QUERY, { fetchPolicy: 'cache-first' } );

    const criteriaTypes = useMemo( () => {
        if ( criteriaTypesData && !criteriaTypesLoading && 
            criteriaTypesData.objectiveCriterionTypes?.edges ) {
            return criteriaTypesData.objectiveCriterionTypes.edges.map( i => {
                return { ...i.node };
            } );
        } else {
            return [];
        }
    }, [ criteriaTypesLoading, criteriaTypesError, criteriaTypesCalled, criteriaTypesData ] );
 
    const handleDeleteClick = async ( id ) => {
        await deleteObjective( id, () => {
            listPageRef.current.reload();
        } );
    };

    const handleCreateClick = () => {
        routeHistory.push( Routes.CreateObjective.buildPath() );
    };

    const handleSetExpandedRows = ( rows ) => {
        expandedRows = rows;
    };

    const renderExpandedRow = ( objective ) => <ObjectiveExpandedRow
        objective={ objective }
        criteriaTypes={ criteriaTypes }
        rewardsEnabled={ { points: POINTS_REWARD_ENABLED, credits: CUSTOM_REWARD_ENABLED, badge: BADGE_REWARD_ENABLED } }
    />;

    const tableColumns = [
        {
            dataIndex: 'name',
            key: 'name',
            title: 'Name',
            sorter: ( a, b ) => 0,
            sortKey: 'name',
            minWidth: 200,
            fixed: 'left',
            render: ( name, i ) => {
                if ( !i.content && !i.name ) {
                    return <span className="no-translation">No translation for this language</span>;
                } else {
                    return (
                        <span className="name-column">
                            { renderWithSearchWords( name, listPageRef.current.getSearchWords() ) }
                        </span> 
                    );
                }
            }
        },
        {
            key: 'rewards',
            title: 'Rewards',
            render: ( _, { credits, points, badgeId } ) => {
                const tags = [];
                if ( points > 0 ) {
                    tags.push( <Tag style={ { borderStyle: POINTS_REWARD_ENABLED ? 'solid' : 'dashed' } } color={ POINTS_REWARD_ENABLED ? 'gold' : '#ddd' }>Points</Tag> );
                }

                if ( credits > 0 ) {
                    tags.push( <Tag style={ { borderStyle: CUSTOM_REWARD_ENABLED ? 'solid' : 'dashed' } } color={ CUSTOM_REWARD_ENABLED ? 'purple' : '#ddd' }>Credits</Tag> );
                }

                if ( badgeId ) {
                    tags.push( <Tag style={ { borderStyle: BADGE_REWARD_ENABLED ? 'solid' : 'dashed' } } color={ BADGE_REWARD_ENABLED ? 'cyan' : '#ddd' }>Badge</Tag> );
                }
                return <>{ tags }</>;
            }
        },
        {
            dataIndex: 'isAvailable',
            key: 'isAvailable',
            title: 'Available',
            render: ( isAvailable ) => isAvailable && <Tag color="green">Available</Tag>
        },
        ...( showBrands ?
            [
                {
                    dataIndex: 'brandIds',
                    key: 'brands',
                    title: 'Brand',
                    sortKey: 'brands',
                    render: ( b ) => {
                        return b.map( i => {
                            const brand = listPageRef.current.getBrand( i );
                            return brand ? <ColoredTag.Brand key={ brand.id } brand={ brand } /> : null;
                        } );
                    }
                },
            ] : [] ),
        {
            dataIndex: 'isPublished',
            key: 'isPublished',
            title: 'Published',
            render: ( isPublished ) => <ColoredTag.Publish isPublished={ isPublished } />
        },
        {
            dataIndex: 'createdAt',
            key: 'createdAt',
            title: 'Created At',
            sorter: ( a, b ) => 0,
            sortKey: 'createdAt',
            render: ( createdAt ) => <span>{ createdAt }</span>
        },
        {
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            title: 'Updated At',
            sorter: ( a, b ) => 0,
            sortKey: 'updatedAt',
            render: ( updatedAt ) => <span>{ updatedAt }</span>
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right', 
            width: '100px',
            render: ( item ) => {
                return (
                    <>
                        <Space>
                            <Button 
                                type="link" 
                                icon={ <EditOutlined /> } 
                                onClick= { () => routeHistory.push( Routes.EditObjective.buildPath( item.id ) ) }
                            />
                            
                            <DeleteButton
                                id={ item.id }   
                                title={ 'Confirm Delete' }
                                disabled={ item.hasRevision }
                                deleteCallback={ ( i ) => handleDeleteClick( i ) }
                                confirmMessage= { 
                                    <>
                                        <p>Are you <i>sure</i> you want to permanently delete the objective <b>{ item.name }</b>? 
                                        This action cannot be undone.</p>
                                    </>
                                }
                            />
                        </Space>
                    </> 
                );
            }   
        }
    ]; 
  
    return (
        <>
            { criteriaTypesError && 
                <Alert 
                    type="error"
                    message="Error"
                    description="An unknown error occurred. Please try again later."
                />
            }

            { /* Loading Spinner */ }
            { criteriaTypesLoading &&
                <Spinner/>
            }

            { criteriaTypesCalled && !criteriaTypesLoading && !criteriaTypesError &&                 
                <SearchQueryList 
                    ref={ listPageRef }
                    type="objectives" 
                    uiStrings={ uiStrings }
                    breadcrumbs={ breadcrumbs }
                    tableColumns={ tableColumns }
                    isDeleting={ isDeleting }
                    renderExpandedRowCallback= { renderExpandedRow }
                    createClickCallback={ handleCreateClick }
                    setExpandedRowsCallback= { handleSetExpandedRows }
                    defaultExpandedRows= { expandedRows }
                />
            }
        </>
    );
};

export default ObjectivesListPage;
